import { FieldState, FormState } from 'formstate';
import { observable } from 'mobx';

import { LoginStore } from '../stores/login.store';
import { RouterStore } from 'src/core/stores/router.store';
import { lazyInject } from '../ioc';
import { BusyFormState } from 'src/util/busy-form';
import { passwordValidator } from 'src/util/validators';

export interface RouteParams {
    token: string;
}

export class PasswordFormState extends BusyFormState {
    @lazyInject(LoginStore) loginStore!: LoginStore;
    @lazyInject(RouterStore) router!: RouterStore;

    @observable showPassword = false;
    @observable errorMessage: string | undefined;

    password = new FieldState('').validators(passwordValidator);

    private token: string | undefined;

    form = new FormState({
        password: this.password,
        isBusy: this.isBusy,
    }).validators(() => (!this.token || !this.loginStore.pwFormPassword) && 'invalid');

    constructor(
        public step: 1 | 2,
        token: string,
        private submitAction: (token: string, email: string, password: string) => Promise<boolean>,
        private submitRoute: string
    ) {
        super();

        if (this.step === 1) {
            this.loginStore.pwFormPassword = undefined;
        } else {
            if (!this.loginStore.pwFormPassword) {
                // go to step 1 if it was skipped
                const path = this.router.location.pathname;
                this.router.history.replace(path.substr(0, path.length - 2));
            } else {
                this.password.validators(
                    (value) => this.loginStore.pwFormPassword !== value && 'noMatch'
                );
            }
        }

        this.form.validate();

        this.loginStore.checkToken(token).then(
            () => {
                this.token = token;
                this.form.validate();
            },
            (err) => (this.errorMessage = err)
        );
    }

    handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (this.step === 1) {
            this.loginStore.pwFormPassword = this.password.$;
            this.router.history.push(`${this.router.location.pathname}/2`);
            return;
        }

        const token = this.token;
        const email = this.loginStore.pwFormEmail;
        const password = this.password.$;

        if (!token || !email || !password) {
            return;
        }

        this.errorMessage = undefined;
        this.withBusy(this.submitAction(token, email, password)).then(
            (success) => {
                if (success) {
                    this.router.history.push(this.submitRoute);
                } else {
                    this.errorMessage = 'Failed to set password';
                }
            },
            (error) => {
                this.errorMessage = error.toString();
            }
        );
    };

    clearError = () => {
        this.errorMessage = undefined;
    };

    toggleShowPassord = () => {
        this.showPassword = !this.showPassword;
    };
}
